import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';
import { hashUserId } from '../../utils/hashUserId';

const MAX_RETRIES = 3;

const fetchUserInfo = async (retries = 0): Promise<string> => {
  const url = `${window.location.origin}/server/get_user`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const { error, state, role, region, userId } = await res.json();
    if (error) throw new Error(error);
    if (!userId) throw new Error('Missing userId in response');

    const userIdForTracking = await hashUserId(userId);

    datadogRum.setUser({
      id: userIdForTracking,
      state: state || 'unknown',
      role: role || 'unknown',
      region: region || 'unknown',
    });

    return userIdForTracking;
  } catch (err: any) {
    if (retries < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return fetchUserInfo(retries + 1);
    } else {
      // Fallback for when user info cannot be fetched
      const fallbackId = 'Unknown';
      datadogRum.setUser({
        id: fallbackId,
        state: 'Unknown',
        role: 'Unknown',
        region: 'Unknown',
      });
      return fallbackId;
    }
  }
};

const DatadogUserInfo = (): JSX.Element => {
  const [userId, setUserId] = useState('');

  // Fetch user info once on mount
  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const executeUserFetch = () => {
      if (userId || !isActive) return;

      fetchUserInfo().then((resolvedId) => {
        if (isActive) {
          setUserId(resolvedId);
        }
      });
    };

    const rumConfig = datadogRum.getInitConfiguration();
    if (rumConfig) {
      executeUserFetch();
    } else {
      unsubscribe = datadogRum.onReady(() => {
        if (!isActive) return;
        executeUserFetch();
      }) as (() => void) | undefined;
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  return <></>;
};

export default DatadogUserInfo;
